import { useEffect, useState } from 'react'

export const STORAGE_KEY = 'vistoera-theme'

export type ThemeMode = 'light' | 'dark' | 'system'
export type EffectiveTheme = 'light' | 'dark'

/** Fired on `document.documentElement` every time `applyThemeToDocument`
 * actually changes the effective theme (explicit pick, or the OS setting
 * changing while `mode === 'system'`) — see `useEffectiveTheme` below.
 * `ThemeToggle` only tracked its own local `mode` state before this, so
 * nothing else in the app (e.g. AntD's `ConfigProvider`) could react to a
 * live theme switch without a full page reload. */
const THEME_CHANGED_EVENT = 'vistoera:theme-changed'

const VALID: ThemeMode[] = ['light', 'dark', 'system']

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function getThemePreference(): ThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw && VALID.includes(raw as ThemeMode)) return raw as ThemeMode
  } catch {
    /* ignore */
  }
  return 'system'
}

export function setThemePreference(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    /* ignore */
  }
  applyThemeToDocument(mode)
}

export function resolveEffectiveTheme(mode: ThemeMode): EffectiveTheme {
  if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light'
  return mode
}

let mediaListener: ((e: MediaQueryListEvent) => void) | null = null
let mediaQuery: MediaQueryList | null = null

function detachSystemListener() {
  if (mediaQuery && mediaListener) {
    mediaQuery.removeEventListener('change', mediaListener)
  }
  mediaQuery = null
  mediaListener = null
}

function attachSystemListener() {
  detachSystemListener()
  if (typeof window === 'undefined' || !window.matchMedia) return
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaListener = () => {
    if (getThemePreference() === 'system') {
      applyThemeToDocument('system')
    }
  }
  mediaQuery.addEventListener('change', mediaListener)
}

export function applyThemeToDocument(mode: ThemeMode): void {
  if (typeof document === 'undefined') return
  const effective = resolveEffectiveTheme(mode)
  const changed = document.documentElement.getAttribute('data-theme') !== effective
  document.documentElement.setAttribute('data-theme', effective)
  document.documentElement.setAttribute('data-theme-preference', mode)
  // Native OS scrollbar / form chrome follows color-scheme — without this,
  // dark theme still paints a white scrollbar track/gutter (macOS "Always").
  document.documentElement.style.colorScheme = effective
  if (mode === 'system') {
    attachSystemListener()
  } else {
    detachSystemListener()
  }
  if (changed) {
    document.documentElement.dispatchEvent(new CustomEvent<EffectiveTheme>(THEME_CHANGED_EVENT, { detail: effective }))
  }
}

/**
 * Reactive read of the current effective (light/dark) theme — for the rare
 * consumer that must actually re-render on a live switch (today: the
 * Workspace shell's nested AntD `ConfigProvider`, see `WorkspaceApp.tsx`),
 * as opposed to `.tc-shell`'s CSS, which already reacts for free via the
 * `[data-theme="dark"]` attribute selector.
 */
export function useEffectiveTheme(): EffectiveTheme {
  const [theme, setTheme] = useState<EffectiveTheme>(() => resolveEffectiveTheme(getThemePreference()))

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<EffectiveTheme>).detail
      setTheme(detail ?? resolveEffectiveTheme(getThemePreference()))
    }
    document.documentElement.addEventListener(THEME_CHANGED_EVENT, onChange)
    return () => document.documentElement.removeEventListener(THEME_CHANGED_EVENT, onChange)
  }, [])

  return theme
}
